import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Confirm } from '~/components/ui/Confirm';
import { JobTemplateFieldsFragment } from '~/gql/graphql';
import { getTemplateSwitchImpact } from './templateSwitchImpact';

type Props = {
  /** Whether to show the warning dialog */
  show: boolean;
  /** The template being switched to */
  pendingTemplate: JobTemplateFieldsFragment | null;
  /** The currently selected template */
  currentTemplate: JobTemplateFieldsFragment | null;
  /** Whether there are existing tasks that will be affected */
  hasExistingTasks: boolean;
  /** Whether there are selected assets that will be cleared */
  hasSelectedAssets: boolean;
  /** Number of selected assets */
  selectedAssetCount?: number;
  /** Number of existing tasks */
  existingTaskCount?: number;
  /** Callback when user confirms the switch */
  onConfirm: () => void;
  /** Callback when user cancels the switch */
  onCancel: () => void;
};

/**
 * TemplateSwitchWarning - Warning dialog for template switching
 *
 * Displays when:
 * - Switching from one template to another with existing tasks
 * - Switching away from an asset-driven template with selected assets
 * - Switching between template types (static <-> asset-driven)
 *
 * Explains what will change, not how the system works.
 */
export const TemplateSwitchWarning = ({
  show,
  pendingTemplate,
  currentTemplate,
  hasExistingTasks,
  hasSelectedAssets,
  selectedAssetCount = 0,
  existingTaskCount = 0,
  onConfirm,
  onCancel,
}: Props) => {
  const { t } = useTranslation();

  const impact = getTemplateSwitchImpact({
    pendingTemplate,
    currentTemplate,
    hasExistingTasks,
    hasSelectedAssets,
  });

  const willLoseTasks = impact?.willLoseTasks ?? false;
  const willLoseAssets = impact?.willLoseAssets ?? false;
  const isChangingBehavior = impact?.isChangingBehavior ?? false;
  const isPendingAssetDriven = impact?.isPendingAssetDriven ?? false;

  // Build the warning message
  // Memoized to avoid recalculating on every render when props remain stable
  // All hooks must be called unconditionally before any early returns
  const warningTitle = useMemo(() => {
    if (!pendingTemplate) {
      return '';
    }
    if (isChangingBehavior) {
      return t('job:templateSwitch.changingType', 'Change template type?');
    }
    return t('job:templateSwitch.switchTemplate', 'Switch template?');
  }, [pendingTemplate, isChangingBehavior, t]);

  // Memoized to avoid recalculating string operations and JSX creation on every render
  // Recomputes only when relevant props change
  const warningBody = useMemo(() => {
    if (!pendingTemplate) {
      return null;
    }

    const changes: string[] = [];

    if (willLoseTasks) {
      changes.push(
        t(
          'job:templateSwitch.willRemoveTasks',
          '{{count}} existing task will be removed',
          {
            count: existingTaskCount,
          }
        )
      );
    }

    if (willLoseAssets) {
      changes.push(
        t(
          'job:templateSwitch.willClearAssets',
          '{{count}} selected asset will be cleared',
          {
            count: selectedAssetCount,
          }
        )
      );
    }

    if (isChangingBehavior) {
      if (isPendingAssetDriven) {
        changes.push(
          t(
            'job:templateSwitch.willRequireAssets',
            'The new template requires asset selection'
          )
        );
      } else {
        changes.push(
          t(
            'job:templateSwitch.willUseFixedTasks',
            'The new template has fixed tasks'
          )
        );
      }
    }

    if (changes.length === 0) {
      return (
        <div>
          {t(
            'job:templateSwitch.defaultWarning',
            'Switching templates will update your current configuration.'
          )}
        </div>
      );
    }

    return (
      <div className='space-y-2'>
        {changes.map((change, index) => (
          <div key={index}>{change}</div>
        ))}
      </div>
    );
  }, [
    pendingTemplate,
    willLoseTasks,
    willLoseAssets,
    isChangingBehavior,
    isPendingAssetDriven,
    existingTaskCount,
    selectedAssetCount,
    t,
  ]);

  // Early return after all hooks have been called
  if (!show || !pendingTemplate) return null;

  return (
    <Confirm
      show={show}
      title={warningTitle}
      cancel={t('job:templateSwitch.keepCurrent', 'Keep current')}
      confirm={t('job:templateSwitch.switchAnyway', 'Switch template')}
      onCancel={onCancel}
      onConfirm={onConfirm}
      danger={willLoseTasks || willLoseAssets}
    >
      {warningBody}
    </Confirm>
  );
};
